/**
 * iframeStore.js
 * Zustand store for iframe visibility state.
 *
 * Lifted OUT of SceneContent so that opening/closing an iframe does NOT
 * trigger a React re-render of the 3D scene tree (which would cause
 * EarthSurfaceMarker's Drei <Html> to re-project at a stale Earth rotation).
 *
 * Only the components that actually need this state subscribe here:
 *  - WebGPUCanvas → reads `showIframe`/`isCrewProfileOpen` to pause the render loop
 *  - Hotspots2D → reads `markersVisible`
 *  - EarthSurfaceMarker sits inside the 3D scene and never subscribes here
 *
 * Note: iframes now open as external tabs (`window.open`), so the original in-app
 * iframe window is gone — this store now mainly carries marker/idle/crew-profile state.
 */
import { create } from 'zustand'

let _closeTimer = null

export const useIframeStore = create((set) => ({
  showIframe: false,
  iframeUrl: '',
  iframeTitle: '',
  isClosingIframe: false,
  // Separate from showIframe — only goes true after the render loop has
  // resumed and Drei <Html> transforms are up-to-date (managed by FrameLoopController)
  markersVisible: true,
  // Incremented each time markers become visible again — used as the key prop
  // on Drei <Html> to force a fresh re-projection after the loop warms up.
  htmlKey: 0,
  setMarkersVisible: (v) => set((state) => ({
    markersVisible: v,
    htmlKey: v ? state.htmlKey + 1 : state.htmlKey,
  })),

  // True while the Crew Profile Screen (2D overlay) is open — pauses the
  // render loop since the 3D scene is fully obscured.
  isCrewProfileOpen: false,
  setCrewProfileOpen: (v) => set({ isCrewProfileOpen: v }),

  openIframe: (url, title = '') => {
    if (_closeTimer !== null) {
      clearTimeout(_closeTimer)
      _closeTimer = null
    }
    set({ iframeUrl: url, iframeTitle: title, isClosingIframe: false, showIframe: true })
  },

  closeIframe: () => {
    set({ isClosingIframe: true })
    _closeTimer = setTimeout(() => {
      _closeTimer = null
      set({ showIframe: false, isClosingIframe: false, iframeUrl: '' })
    }, 400)
  },
}))
